export const en = {
  metaTitle: "Billing & Subscription - OrderHub",
  metaDescription: "Manage your plan, monitor usage, and view invoices.",
  title: "Billing & Subscription",
  breadcrumb: "Billing",
  subtitle: "Manage your plan, monitor usage, and view invoices",
  syncButton: "Sync",
  syncSyncing: "Syncing...",
  syncSynced: "Synced",
  syncRetry: "Retry",
  lastSynced: "Last synced: {{time}}",
  planUpgradedToast: "Plan upgraded successfully! Invite your team to get started.",
  syncSuccessToast: "Subscription synced successfully",
  syncFailedToast: "Failed to sync subscription",
  syncErrorNoStore: "No store connected. Connect a store to sync billing.",
  syncErrorNoSubscription: "No active subscription found on Shopify.",
  syncErrorGeneric: "Failed to sync. Please try again.",
  syncErrorTokenExpired: "Store access token expired. Please reconnect your store.",
  statCard: {
    activeUsers: "Active Users",
    billingPeriod: "Billing Period",
    quickActions: "Quick Actions",
  },
  upgradePlan: "Upgrade Plan",
  managePlanOnShopify: "Manage Plan on Shopify",
  needMoreUsersContact: "Need more than {{limit}} users? Contact us",
  billingDetails: "Invoices & Billing Details",
  billingDetailsSection: {
    connectedTitle: "View your invoices and manage billing directly in Shopify",
    connectedDescription:
      "Your subscription and payment history are handled securely by Shopify.",
    disconnectedDescription:
      "Connect your Shopify store to view invoices and billing details.",
    viewInShopify: "View billing in Shopify",
    connectStore: "Connect Store",
  },
  paymentIssueBannerTitle: "Payment issue detected",
  paymentIssueBannerDescription:
    "Your subscription has a payment issue. Please select a plan below to restore billing.",
  noStoreBannerTitle: "Connect a Shopify store to get started",
  noStoreBannerDescription:
    "Billing is managed through Shopify. Connect a store first to select a plan and activate your subscription.",
  noStoreBannerAction: "Connect Store",
  trustBadge: {
    secureBilling: "Secure billing via Shopify",
    proratedChanges: "Prorated changes",
    syncAnytime: "Sync anytime",
  },
  hero: {
    usersBadge: "{{count}} users",
    usersBadgeTip: "Your plan includes up to {{limit}} active team members",
    shopifyBillingBadge: "Shopify Billing",
    shopifyBillingTip: "Your subscription is managed through Shopify",
    freeForever: "Free forever",
    renewsOn: "Renews {{date}}",
    free: "Free",
    upgradePlan: "Upgrade Plan",
    managePlan: "Manage Plan",
    cancelling: "Subscription cancelling",
    endsOn: " — your plan ends on {{date}}",
    keepMyPlan: "Keep My Plan",
    seatsUsed: "seats used",
    seatsUsedTip: "{{active}} of {{limit}} user seats are currently active",
    stores: "stores",
    storesTip: "{{count}} store(s) connected to your workspace",
    activitiesThisMonth: "activities this month",
    activitiesTip: "{{count}} order activities recorded this month",
    daysLeft: "days left",
    connectStoreBannerTitle: "Connect your Shopify store to enable billing",
    connectStoreBannerDescription:
      "Billing is managed through Shopify. Connect a store to proceed.",
    connectStoreBannerAction: "Connect Store",
  },
  intervalToggle: {
    monthly: "Monthly",
    annual: "Annual",
    save20: "Save 20%",
  },
  priceSuffix: {
    monthlyShort: "mo",
    yearlyShort: "yr",
  },
  usageMeter: {
    of: "of",
    activeSeatTip: "Active seat #{{index}}",
    seatsUsedTip: "You've used {{current}} of {{limit}} available user seats",
    activities: "Activities",
    thisMonth: "this month",
    stores: "Stores",
    connected: "connected",
  },
  comparison: {
    title: "Choose Your Plan",
    description: "You'll be redirected to Shopify to complete your plan selection.",
    popular: "Popular",
    free: "Free",
    default: "Default",
    select: "Select",
    currentPlan: "Current Plan",
    featuresHeader: "Features",
    freePlanError: "The Free plan is included by default. To downgrade, cancel your current subscription from your Shopify admin.",
    retry: "Retry",
    redirecting: "Redirecting to Shopify...",
    secureBilling: "Secure billing",
    proratedChanges: "Prorated changes",
    cancelAnytime: "Cancel anytime",
    changesNextCycle: "Changes apply at next billing cycle",
  },
  downgrade: {
    successTitle: "Plan Downgraded",
    successBody: "Your plan has been changed to {{planName}} effective immediately.",
    overLimitTitle: "Users Exceed Plan Limit",
    overLimitDescription: "Remove team members before downgrading to {{planName}}",
    actionRequired: "Action Required",
    removeUsersBefore: "Remove {{count}} user(s) before downgrading",
    currentCount: "Current: {{count}}",
    limitCount: "Limit: {{count}}",
    featuresYouWillLose: "Features you'll lose",
    manageUsers: "Manage Users",
    title: "Downgrade from {{planName}}",
    immediateDescription: "This change takes effect immediately",
    immediateLabel: "Immediate downgrade",
    keepPlan: "Keep {{planName}}",
    redirecting: "Redirecting to Shopify...",
    downgradeNow: "Downgrade Now",
    teamCapacity: "Team capacity: {{from}} → {{to}} member(s)",
    immediateChangeBody: "Your plan will change from {{planName}} to {{targetPlanName}} right away. You will lose access to {{planName}}-only features immediately.",
    managerConflictTitle: "Manager Roles Exceed Plan Features",
    managerConflictDescription: "The {{planName}} plan does not include Team Management",
    demoteManagersBefore: "Demote {{count}} manager-role user(s) to Rep or remove them before downgrading",
    managerCount: "Managers: {{count}}",
    managerHint: "Manager features (Team Inbox, Reports, Customers) require the Growth plan or above",
  },
  tierStrip: {
    popular: "Popular",
    free: "Free",
    upgrade: "Upgrade",
    select: "Select",
    currentPlan: "Current Plan",
    switchToAnnual: "Switch to Annual",
    switchToMonthly: "Switch to Monthly",
    perMonthEquiv: "/mo equiv",
    user: "User",
  },
  periodCard: {
    noActivePeriod: "No active period",
    upgradeToActivate: "Upgrade to activate billing",
    daysRemaining: "days remaining",
    expiringSoon: "Expiring soon",
    autoRenews: "Auto-renews",
    endsSoonTooltip: "Your billing period ends soon — it will auto-renew",
    autoRenewTooltip: "Your subscription will automatically renew at period end",
    managedVia: "Managed via",
  },
  invoices: {
    totalPaid: "Total Paid",
    invoices: "Invoices",
    lastPayment: "Last Payment",
    empty: "No invoices yet",
    emptyDescription: "Invoices will appear here once you have an active paid subscription",
    downloadPdf: "Download PDF",
    columns: {
      date: "Date",
      invoice: "Invoice",
      amount: "Amount",
      status: "Status",
      pdf: "PDF",
    },
    status: {
      paid: "Paid",
      pending: "Pending",
      failed: "Failed",
    },
  },
  pagination: {
    showing: "Showing",
    of: "of",
    invoices: "invoices",
    rows: "Rows",
    rowsPerPage: "Rows per page",
  },
  plans: {
    free: { name: "Free", label: "For solo entrepreneurs" },
    starter: { name: "Starter", label: "For small teams" },
    growth: { name: "Growth", label: "For growing businesses" },
    scale: { name: "Scale", label: "For scaling operations" },
  },
  features: {
    team_members: "Users",
    order_management: "Order Management",
    basic_analytics: "Basic Analytics",
    priority_support: "Priority Support",
    team: "Team Management",
    vip_customers: "VIP Customers",
    account_manager: "Dedicated Account Manager",
  },
} as const;
